import React, { forwardRef } from 'react';
import { useMemo } from 'react';
import Icon from '../Icon';
import { InputProps } from './types/Input.types';
import clsx from 'clsx';

const Input: React.FC<
  InputProps & React.InputHTMLAttributes<HTMLInputElement>
> = forwardRef<
  HTMLInputElement,
  InputProps & React.InputHTMLAttributes<HTMLInputElement>
>(
  (
    {
      inputSize = 'medium',
      inputState = 'default',
      icon,
      className,
      wrapperClassName,
      label,
      helperText,
      message,
      showMessageIcon = true,
      showAnimatedLabel = false,
      ...props
    },
    ref
  ) => {
    const messageIcon = useMemo(() => {
      switch (inputState) {
        case 'default':
        default:
          return null;

        case 'success':
          return <Icon name="check-circle" />;

        case 'error':
        case 'warning':
          return <Icon name="warning" />;
      }
    }, [inputState]);

    const sizeClass = useMemo(() => {
      switch (inputSize) {
        case 'small':
        case 'xsmall':
          return 'input-wrapper--sm';
        case 'large':
          return 'input-wrapper--lg';
        case 'xlarge':
          return 'input-wrapper--xl';
        case 'medium':
        default:
          return 'input-wrapper--md';
      }
    }, [inputSize]);

    const animatedClass = useMemo(() => {
      switch (inputSize) {
        case 'medium':
          return 'input-with-animated-label-medium';
        default:
          return 'input-with-animated-label';
      }
    }, [inputSize]);

    const stateClass = useMemo(() => {
      switch (inputState) {
        case 'error':
          return 'input-wrapper--error';
        case 'warning':
          return 'input-wrapper--warning';
        case 'success':
          return 'input-wrapper--success';
        default:
          return undefined;
      }
    }, [inputState]);

    return (
      <div
        className={clsx(
          wrapperClassName,
          'input-wrapper',
          sizeClass,
          stateClass,
          !!icon && 'input-wrapper--has-icon'
        )}
      >
        {label && !showAnimatedLabel && (
          <label htmlFor={props.id}>
            {label}
            {props.required && '*'}
          </label>
        )}
        <div className="relative flex">
          <input
            placeholder=" "
            {...props}
            className={clsx(
              className,
              'input',
              showAnimatedLabel ? animatedClass : ''
            )}
            ref={ref}
          />
          {typeof icon == 'string' && <Icon name={icon} />}
          {typeof icon == 'object' && icon}
          {label && showAnimatedLabel && (
            <label htmlFor={props.id}>
              {label}
              {props.required && '*'}
            </label>
          )}
        </div>
        {helperText && <span className="helper-text">{helperText}</span>}
        {message && (
          <span className="message">
            {showMessageIcon && messageIcon}
            <span>{message}</span>
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
